import {
  ChevronDown,
  ChevronRight,
  CircleCheck,
  Code2,
  Eye,
  FileText,
  HelpCircle,
  Image as ImageIcon,
  List,
  ListChecks,
  Loader2,
  Pencil,
  Plus,
  Globe,
  Search,
  Sparkles,
  Terminal,
  Trash2,
  Upload,
} from "lucide-react";
import { useEffect, useState, type ComponentType } from "react";

import { cn } from "@bigpp/ui/lib/utils";

import type { ToolCallCard } from "../agent/useAgentRun.ts";
import { fetchSlideImage, type SlideImage } from "../bridge/slideImage.ts";

type IconType = ComponentType<{ className?: string }>;

const TOOL_META: Record<ToolCallCard["toolName"], { icon: IconType; baseLabel: string }> = {
  execute_office_js_code: { icon: Code2, baseLabel: "execute_office_js_code" },
  upload_current_deck_to_sandbox: { icon: Upload, baseLabel: "upload_current_deck_to_sandbox" },
  insert_deck_slides: { icon: FileText, baseLabel: "insert_deck_slides" },
  peek_slide: { icon: ImageIcon, baseLabel: "peek_slide" },
  use_skill: { icon: Sparkles, baseLabel: "use_skill" },
  ask_user: { icon: HelpCircle, baseLabel: "ask_user" },
  create_skill: { icon: Plus, baseLabel: "create_skill" },
  edit_skill: { icon: Pencil, baseLabel: "edit_skill" },
  delete_skill: { icon: Trash2, baseLabel: "delete_skill" },
  list_skills: { icon: List, baseLabel: "list_skills" },
  insert_attachment_image: { icon: ImageIcon, baseLabel: "insert_attachment_image" },
  insert_sandbox_image: { icon: ImageIcon, baseLabel: "insert_sandbox_image" },
  insert_sandbox_deck_slides: { icon: FileText, baseLabel: "insert_sandbox_deck_slides" },
  code_interpreter: { icon: Terminal, baseLabel: "code_interpreter" },
  file_search: { icon: Search, baseLabel: "file_search" },
  web_search: { icon: Globe, baseLabel: "web_search" },
  create_plan: { icon: ListChecks, baseLabel: "create_plan" },
  jsx_preview: { icon: Eye, baseLabel: "jsx_preview" },
};

// Snake/kebab → Sentence case: "execute_office_js_code" → "Execute office js code",
// "swedish-legal-docs" → "Swedish legal docs". Used for both tool names and
// skill slugs so the panel reads like a human progress log, not an API trace.
export const humanize = (s: string): string => {
  const spaced = s.replace(/[-_]+/g, " ").trim();
  if (spaced.length === 0) return s;
  return spaced.charAt(0).toUpperCase() + spaced.slice(1).toLowerCase();
};

export const labelFor = (tc: ToolCallCard): string => {
  switch (tc.toolName) {
    case "execute_office_js_code":
      return "Working with deck";
    case "peek_slide":
      return `Peek slide · slide ${(tc.slideIndex + 1).toString()}`;
    case "use_skill":
      return `Use skill · ${humanize(tc.slug)}`;
    case "ask_user":
      return tc.status === "running" ? "Waiting for your answer" : "Asked you a question";
    case "upload_current_deck_to_sandbox":
      return tc.result
        ? `Upload deck to sandbox · ${tc.result.slideCount.toString()} slides`
        : `Upload deck to sandbox`;
    case "insert_deck_slides":
      return `Insert deck slides`;
    case "create_skill":
      return `Create skill · ${humanize(tc.slug)}`;
    case "edit_skill":
      return `Edit skill · ${humanize(tc.slug)}`;
    case "delete_skill":
      return `Delete skill · ${humanize(tc.slug)}`;
    case "list_skills":
      return `List skills`;
    case "insert_attachment_image":
      return `Insert attachment image · slide ${(tc.slideIndex + 1).toString()}`;
    case "insert_sandbox_image":
      return `Insert sandbox image · ${tc.filename} → slide ${(tc.slideIndex + 1).toString()}`;
    case "insert_sandbox_deck_slides":
      return `Insert sandbox deck slides · ${tc.filename}`;
    case "code_interpreter":
      return `Running code`;
    case "file_search":
      return tc.queries && tc.queries.length > 0
        ? `File search · ${tc.queries[0] ?? ""}`
        : `File search`;
    case "web_search":
      return tc.queries && tc.queries.length > 0
        ? `Web search · ${tc.queries[0] ?? ""}`
        : `Web search`;
    case "create_plan":
      return tc.title ? `Plan · ${tc.title}` : `Plan`;
    case "jsx_preview":
      return tc.title ? `Preview · ${tc.title}` : `Preview`;
  }
};

export function ToolCard({ tc, defaultOpen = false }: { tc: ToolCallCard; defaultOpen?: boolean }) {
  const [open, setOpen] = useState(defaultOpen);
  const { icon: Icon } = TOOL_META[tc.toolName];

  const StatusIcon = tc.status === "running" ? Loader2 : CircleCheck;
  const statusClass =
    tc.status === "running"
      ? "text-[var(--color-muted-foreground)] animate-spin"
      : "text-[var(--color-success)]";

  return (
    <div className="rounded-md border border-[var(--color-border)] bg-[var(--color-card)] text-xs overflow-hidden">
      <button
        type="button"
        onClick={() => {
          setOpen((o) => !o);
        }}
        className="flex w-full items-center justify-between gap-2 px-2.5 py-1.5 hover:bg-[var(--color-secondary)] transition-colors"
      >
        <span className="flex items-center gap-2 min-w-0">
          <StatusIcon className={cn("size-3.5 shrink-0", statusClass)} />
          <Icon className="size-3.5 shrink-0 text-[var(--color-muted-foreground)]" />
          <span className="truncate font-mono text-[11px]">{labelFor(tc)}</span>
        </span>
        {open ? (
          <ChevronDown className="size-3.5 text-[var(--color-muted-foreground)]" />
        ) : (
          <ChevronRight className="size-3.5 text-[var(--color-muted-foreground)]" />
        )}
      </button>
      {open && (
        <div className="border-t border-[var(--color-border)] p-2">
          <ToolCardDetails tc={tc} />
        </div>
      )}
    </div>
  );
}

// Body-only view of a tool call's details — used by TaskRow which already
// has its own header chrome.
export function ToolCardDetails({ tc }: { tc: ToolCallCard }) {
  return (
    <div className="flex flex-col gap-2">
      {tc.toolName === "execute_office_js_code" && (
            <>
              {tc.result && !tc.result.ok && tc.result.error.hint && (
                <Section label="hint">
                  <Pre variant="error">
                    {[tc.result.error.code, tc.result.error.hint, tc.result.error.nextStep]
                      .filter(Boolean)
                      .join("\n")}
                  </Pre>
                </Section>
              )}
              {tc.result && (
                <Section label="result">
                  <Pre>{JSON.stringify(tc.result, null, 2)}</Pre>
                </Section>
              )}
              {tc.error && (
                <Section label="error">
                  <Pre variant="error">{tc.error}</Pre>
                </Section>
              )}
            </>
          )}
          {(tc.toolName === "insert_deck_slides" ||
            tc.toolName === "upload_current_deck_to_sandbox") && (
            <>
              {tc.result && (
                <Section label="result">
                  <Pre>{JSON.stringify(tc.result, null, 2)}</Pre>
                </Section>
              )}
              {tc.error && (
                <Section label="error">
                  <Pre variant="error">{tc.error}</Pre>
                </Section>
              )}
            </>
          )}
          {tc.toolName === "code_interpreter" && (
            <>
              {tc.code ? (
                <Section label="code">
                  <Pre>{tc.code}</Pre>
                </Section>
              ) : (
                <div className="text-[var(--color-muted-foreground)]">
                  Running Python in OpenAI's sandbox…
                </div>
              )}
            </>
          )}
          {tc.toolName === "file_search" && (
            <>
              {tc.queries && tc.queries.length > 0 ? (
                <Section label="queries">
                  <Pre>{tc.queries.join("\n")}</Pre>
                </Section>
              ) : (
                <div className="text-[var(--color-muted-foreground)]">
                  Searching your uploaded files…
                </div>
              )}
            </>
          )}
          {tc.toolName === "web_search" && (
            <>
              {tc.queries && tc.queries.length > 0 ? (
                <Section label="queries">
                  <Pre>{tc.queries.join("\n")}</Pre>
                </Section>
              ) : (
                <div className="text-[var(--color-muted-foreground)]">
                  Searching the web…
                </div>
              )}
            </>
          )}
          {(tc.toolName === "insert_attachment_image" ||
            tc.toolName === "insert_sandbox_image" ||
            tc.toolName === "insert_sandbox_deck_slides") && (
            <>
              {tc.result && (
                <Section label="result">
                  <Pre>{JSON.stringify(tc.result, null, 2)}</Pre>
                </Section>
              )}
              {tc.error && (
                <Section label="error">
                  <Pre variant="error">{tc.error}</Pre>
                </Section>
              )}
            </>
          )}
      {tc.toolName === "peek_slide" && (
        <>
          {tc.status === "ok" && (
            <PeekSlideImage
              slideIndex={tc.slideIndex}
              base64={tc.base64}
              mediaType={tc.mediaType}
            />
          )}
          {tc.error && (
            <Section label="error">
              <Pre variant="error">{tc.error}</Pre>
            </Section>
          )}
        </>
      )}
      {(tc.toolName === "use_skill" ||
        tc.toolName === "create_skill" ||
        tc.toolName === "edit_skill" ||
        tc.toolName === "delete_skill" ||
        tc.toolName === "list_skills" ||
        tc.toolName === "create_plan" ||
        tc.toolName === "jsx_preview") && null}
    </div>
  );
}

function PeekSlideImage({
  slideIndex,
  base64,
  mediaType,
}: {
  slideIndex: number;
  base64?: string;
  mediaType?: string;
}) {
  const [img, setImg] = useState<SlideImage | null>(
    base64 && mediaType ? { base64, mediaType } : null,
  );
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    if (base64 && mediaType) {
      setImg({ base64, mediaType });
      setErr(null);
      return;
    }
    if (img) return;
    let cancelled = false;
    fetchSlideImage(slideIndex, { width: 960 }).then(
      (c) => {
        if (!cancelled) setImg(c);
      },
      (e: unknown) => {
        if (!cancelled) setErr(e instanceof Error ? e.message : String(e));
      },
    );
    return () => {
      cancelled = true;
    };
  }, [slideIndex, base64, mediaType, img]);

  if (img) {
    return (
      <img
        src={`data:${img.mediaType};base64,${img.base64}`}
        alt={`Slide ${String(slideIndex + 1)}`}
        className="w-full rounded border border-[var(--color-border)]"
      />
    );
  }
  if (err) {
    return (
      <div className="px-1 py-2 text-[10px] text-[var(--color-muted-foreground)]">
        Preview unavailable
      </div>
    );
  }
  return (
    <div className="px-1 py-2 text-[10px] text-[var(--color-muted-foreground)]">Loading…</div>
  );
}

function Section({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-1">
      <div className="text-[10px] uppercase tracking-wider text-[var(--color-muted-foreground)]">
        {label}
      </div>
      {children}
    </div>
  );
}

function Pre({
  children,
  variant = "default",
}: {
  children: React.ReactNode;
  variant?: "default" | "error";
}) {
  return (
    <pre
      className={cn(
        "m-0 max-h-48 overflow-auto rounded bg-[var(--color-secondary)] p-2 text-[11px] whitespace-pre-wrap break-words font-mono",
        variant === "error" && "text-[var(--color-destructive)]",
      )}
    >
      {children}
    </pre>
  );
}
